/**
 * Per-card scheduling FACTS — the handful of values every Home-screen count is
 * a function of, extracted once per card so a deck can be summarised without
 * keeping its card bodies in memory.
 *
 * Each fact is defined by the card-based function it stands in for, named
 * beside it. The summary counters (`./counters.ts`) are pinned EXACTLY equal to
 * those functions by `counters.spec.ts`; when one of them changes, change the
 * fact here and let the pin tell you whether the two still agree.
 */
import { isCardDeleted } from '../../algorithms/spaced-repetition/card-utils';
import { FLASHKA_LOADING_CARD_FRONT_PLACEHOLDER } from '../../algorithms/spaced-repetition/deck-visibility';
import { isCardLeech, lastAlgoInfo } from '../../algorithms/spaced-repetition/scheduling';
import type { SpacedRepetitionCard } from '../../algorithms/spaced-repetition/types';

/** The card fields the summary reads. Both apps' card types and the sync
 *  store's `SyncCardProps` satisfy it structurally. */
export type SummaryCard = Pick<
  SpacedRepetitionCard,
  'front' | 'deletedTimeISO' | 'actions' | 'studyState' | 'tags' | 'starred'
>;

export interface CardSchedulingFacts {
  /** `filterVisibleStudyCards`: not deleted, not a "Loading..." stub. Every
   *  other fact below is only consulted for visible cards. */
  visible: boolean;
  /** `studyState.length > 0` — a visible card without it is "new". */
  hasState: boolean;
  /** `isCardLeech` (a leech tag on a non-deleted card). */
  leech: boolean;
  /** `lastAlgoInfo(card) !== undefined` — the last scheduling entry can be read.
   *  `isMemorisedCard` refuses a card without it; `getCardLabel` does not. */
  readableAlgoInfo: boolean;
  /** `Date.parse(lastAlgoInfo.nextDueISO)`, or null when absent or unparseable
   *  — exactly the instant `isCardDue` compares with `isBefore` (date-fns v4
   *  parses a string argument with `new Date`). */
  dueMs: number | null;
  /** `studyState[0].ISO` as ms — the instant `isCardFirstStudiedToday` reads —
   *  or null when the card has no history, its first entry is unreadable, or it
   *  carries any `deletedTimeISO` (that function checks `!= null`, so an empty
   *  string also disqualifies). */
  firstStudiedMs: number | null;
  /** `getCardLastStudiedTime` as ms, over readable entries; 0 when none.
   *  Also 0 when ANY entry's timestamp is unparseable: that function's
   *  `Math.max` turns into NaN and `deckLastStudiedAtMs` then skips the card.
   *  Read for every row, deleted included — `deckLastStudiedAtMs` does not
   *  filter. */
  lastStudiedMs: number;
  /** `card.starred && !isCardDeleted(card)` (the `star` label). */
  starred: boolean;
  /** The card was ever answered good/easy — `countStudiedCards`'s predicate
   *  (the explore "study 10 cards" task). Read for every row, deleted or
   *  loading included: that function does not filter. */
  everGood: boolean;
}

const parseMs = (iso: unknown): number | null => {
  if (typeof iso !== 'string' || iso.length === 0) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Cached per card OBJECT. A card is immutable once the store publishes it —
 * every edit builds a new card (the identity-based dirty scan depends on it) —
 * so one edit on a 5k-card deck re-derives ONE card's facts when the bridge
 * re-summarises the bucket, not 5k (each a walk of its whole study history).
 * The returned object is shared: read it, never write it.
 */
const factsByCard = new WeakMap<object, CardSchedulingFacts>();

export function cardSchedulingFacts(card: SummaryCard): CardSchedulingFacts {
  let facts = factsByCard.get(card);
  if (facts === undefined) {
    facts = computeCardSchedulingFacts(card);
    factsByCard.set(card, facts);
  }
  return facts;
}

/** Uncached {@link cardSchedulingFacts} — exported for the equality pins. */
export function computeCardSchedulingFacts(card: SummaryCard): CardSchedulingFacts {
  const deleted = isCardDeleted(card);
  const visible = !deleted && card.front !== FLASHKA_LOADING_CARD_FRONT_PLACEHOLDER;
  // Defensive read: the summary flush runs in a timer at every launch, so a
  // throw here on one malformed card is a crash that repeats on relaunch.
  const studyState = Array.isArray(card.studyState) ? card.studyState : [];
  const hasState = studyState.length > 0;

  // `getCardLastStudiedTime`: readable entries only (skips holes and entries
  // without a string ISO), then the max — NaN-poisoned when any parse fails.
  let lastStudiedMs = 0;
  let poisoned = false;
  for (let i = 0; i < studyState.length; i += 1) {
    const entry = studyState[i] as SpacedRepetitionCard['studyState'][number] | undefined;
    if (typeof entry?.ISO !== 'string') continue;
    const ms = new Date(entry.ISO).getTime();
    if (Number.isNaN(ms)) {
      poisoned = true;
      break;
    }
    if (ms > lastStudiedMs) lastStudiedMs = ms;
  }
  if (poisoned) lastStudiedMs = 0;

  let everGood = false;
  for (let i = 0; i < studyState.length; i += 1) {
    const type = (studyState[i] as { type?: string } | undefined)?.type;
    if (type === 'good' || type === 'easy') {
      everGood = true;
      break;
    }
  }

  if (!visible) {
    return {
      visible,
      hasState,
      leech: false,
      readableAlgoInfo: false,
      dueMs: null,
      firstStudiedMs: null,
      lastStudiedMs,
      starred: false,
      everGood,
    };
  }

  const algoInfo = hasState ? lastAlgoInfo(card) : undefined;
  const first = hasState
    ? (studyState[0] as SpacedRepetitionCard['studyState'][number] | undefined)
    : undefined;

  return {
    visible,
    hasState,
    leech: hasState && isCardLeech(card),
    readableAlgoInfo: algoInfo !== undefined,
    dueMs: hasState ? parseMs(algoInfo?.nextDueISO) : null,
    firstStudiedMs: hasState && card.deletedTimeISO == null ? parseMs(first?.ISO) : null,
    lastStudiedMs,
    starred: card.starred === true,
    everGood,
  };
}
